/**
 * Offers, referrals and Punya.
 *
 * **All three tabs are live** (E31, E32): campaigns, the referral programme, and the Punya
 * liability with per-devotee statements. The monthly Punya issued/redeemed/expired ledger the
 * fixture once showed is ABSENT, not ported — no endpoint yields it, and a plausible table
 * nobody can trace to a row is the thing a console must never render.
 *
 * **Nothing here recomputes a discount, a status or a cap.** The server decides all three: a
 * campaign closes itself inside the transaction that takes its last redemption, and the four-eyes
 * threshold is a settings row read from the API. A console keeping its own copy of the threshold
 * would print a number that was right on the day it was written and wrong the first time ops
 * moved the dial — and it would say so confidently, next to a button.
 *
 * **`held` and `spent` stay apart.** One is money committed to bookings that may still die; the
 * other has left the business. Summed into one "used" figure they would hide a campaign whose
 * whole budget is held by carts nobody has paid for, which reads as spent and is recoverable.
 *
 * Two boundaries the screen still holds: public offer copy is content-linted server-side, and
 * growth cannot move Punya alone — that is a finance act with four eyes.
 */
import { useState } from 'react';
import { Chip, Kicker, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable, DetailPage, Pager } from '../components/data';
import { AuditTrailPanel } from '../components/audit';
import { KV } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ApiError, errorMessage } from '../lib/api/client';
import { ConfirmAction, Money, ScopeAction, Section, StatTile } from '../components/kit';
import { formatInr, parsePaise } from '../lib/money';
import { count } from '../lib/text';
import { ratioPct } from '../lib/pct';
import { useConsole } from '../app/console';
import {
  useCreateOffer,
  useOfferScopeOptions,
  useMintCoupon,
  useOfferCoupons,
  useOfferSpend,
  useOffers,
  useSetOfferStatus,
} from '../lib/api/hooks/offers';
import type { OfferRow } from '../lib/api/hooks/offers';
import { useSetting } from '../lib/api/hooks/settings';
import {
  useAdjustPunya,
  usePunyaLiability,
  usePunyaStatement,
  useReferral,
  useReferrals,
  useRejectReferral,
  useRequalifyReferral,
} from '../lib/api/hooks/loyalty';
import { AbuseTab, OfferEditor, termLines } from './OffersEdit';
import { OfferRedemptions } from './OfferRedemptions';
import { OfferScopeFields, scopeSummary } from './OfferScope';
import { useUrlTab } from '../lib/filterCodecs';

const TABS = [
  { key: 'offers', label: 'Offers' },
  { key: 'referrals', label: 'Referrals & fraud' },
  { key: 'punya', label: 'Punya ledger' },
  { key: 'abuse', label: 'Abuse report' },
];

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same tab. */
const TAB_KEYS = TABS.map((t) => t.key);

const PAGE_SIZE = 25;

/** A typed integer id, or null. Shared so an empty box means "nothing looked up" in one place
    rather than in four, where the fourth eventually reads `Number('')` as user zero. */
export function digits(raw: string): number | null {
  const trimmed = raw.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** The server's word for each state, and the tone it deserves. */
const STATUS_TONE: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
  active: 'success',
  draft: 'neutral',
  paused: 'warn',
  exhausted: 'error',
  ended: 'neutral',
};

/** Burn against the cap. A share of two integers is not money arithmetic — the cap is the server's. */
export function burnRatio(spent: number, budget: number): number {
  return budget ? spent / budget : 0;
}

/**
 * What a campaign takes off, in the units the server actually stores.
 *
 * `discount_value` carries paise for `flat` and BASIS POINTS for `percent` — one integer column,
 * two units. Rendering it without that branch would price a 10% offer as ₹10.
 */
export function describeDiscount(
  offer: Pick<OfferRow, 'discount_kind' | 'discount_value'>,
): string {
  return offer.discount_kind === 'percent'
    ? `${(offer.discount_value / 100).toFixed(2)}%`
    : formatInr(offer.discount_value);
}

function BurnBar({ ratio }: { ratio: number }) {
  const colour =
    ratio > 0.9 ? 'var(--c-error)' : ratio > 0.7 ? 'var(--c-warn)' : 'var(--c-success)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-sm)' }}>
      <div
        role="progressbar"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Budget burn"
        style={{
          width: 90,
          height: 6,
          borderRadius: 'var(--r-pill)',
          background: 'var(--c-sunk)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${ratio * 100}%`,
            height: '100%',
            background: colour,
          }}
        />
      </div>
      <span
        className="tabular"
        style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}
      >
        {ratioPct(ratio)}
      </span>
    </div>
  );
}

/**
 * Mint a code under a campaign.
 *
 * The server owns both refusals this can hit — a code already taken anywhere (409) and an
 * auto-apply campaign that takes no codes at all (422). Neither is pre-empted here beyond hiding
 * the form on an auto-apply offer, where the action is absent rather than disabled: a button that
 * can never work is worse than no button.
 */
function MintCode({ offerId, takesCodes }: { offerId: number; takesCodes: boolean }) {
  const { scope } = useConsole();
  const [code, setCode] = useState('');
  const mint = useMintCoupon(offerId);

  if (!takesCodes) return null;

  return (
    <Section title="Mint a code" note="Codes are unique across every campaign">
      <Input
        density="admin"
        label="Code"
        value={code}
        onChange={setCode}
        placeholder="DIWALI10"
        helper="Stored uppercase — 4 to 24 characters"
      />
      {mint.isError && (
        <ErrorState inline title="The server refused this code" cause={errorMessage(mint.error)} />
      )}
      <ScopeAction
        scope={scope}
        need="growth"
        size="sm"
        disabled={code.trim().length < 4}
        onClick={() => mint.mutate({ code: code.trim() }, { onSuccess: () => setCode('') })}
      >
        Mint code
      </ScopeAction>
    </Section>
  );
}

/**
 * Create a campaign.
 *
 * **The four-eyes refusal is the server's to make.** A budget over the ceiling comes back as a 409
 * naming the threshold, and the form shows that verbatim rather than pre-checking against a number
 * it would have to keep in step with the settings row. The same goes for the percent rules: an
 * uncapped percentage and one above 100% are both 422s with their own codes.
 */
function NewOffer({ onCreated }: { onCreated: () => void }) {
  const { scope } = useConsole();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [terms, setTerms] = useState('');
  const [kind, setKind] = useState('flat');
  const [value, setValue] = useState('');
  const [cap, setCap] = useState('');
  const [budget, setBudget] = useState('');
  const [firstOnly, setFirstOnly] = useState('Anyone');
  const [perUser, setPerUser] = useState('1');
  const [seconder, setSeconder] = useState('');
  const [cityIds, setCityIds] = useState<number[]>([]);
  const [pujaIds, setPujaIds] = useState<number[]>([]);
  const create = useCreateOffer();

  /* Every ₹ field takes rupees and is sent as paise; basis points stay whole numbers. A blank
     cap or budget is null (none), and anything unreadable holds the button rather than guessing. */
  const amount =
    kind === 'percent' ? (/^\d+$/.test(value.trim()) ? Number(value) : null) : parsePaise(value);
  const capPaise = parsePaise(cap);
  const budgetPaise = parsePaise(budget);
  const amountError =
    value.trim() === '' || amount !== null
      ? undefined
      : kind === 'percent'
        ? 'Whole basis points'
        : 'Rupees, up to two decimals';
  const unreadable = (raw: string, paise: number | null) =>
    raw.trim() !== '' && (paise === null || paise <= 0);
  const ready =
    slug.trim() !== '' &&
    name.trim() !== '' &&
    title.trim() !== '' &&
    amount !== null &&
    amount > 0 &&
    !unreadable(cap, capPaise) &&
    !unreadable(budget, budgetPaise);
  const needsSecond =
    create.error instanceof ApiError && create.error.code === 'second_approval_required';

  return (
    <Section
      title="New campaign"
      note="A large budget parks for a second approver — the server decides, not this form"
    >
      <Input
        density="admin"
        label="Slug"
        value={slug}
        onChange={setSlug}
        placeholder="diwali-2026"
      />
      <Input
        density="admin"
        label="Name (internal)"
        value={name}
        onChange={setName}
        placeholder="Diwali 2026"
      />
      <Input density="admin" label="Headline (public, linted)" value={title} onChange={setTitle} />
      <Input
        density="admin"
        label="Terms (public, linted)"
        multiline
        value={terms}
        onChange={setTerms}
        helper="One line per term, shown verbatim under the offer's Terms link · blank hides the link"
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <Select
          density="admin"
          label="Kind"
          options={['flat', 'percent']}
          value={kind}
          onChange={setKind}
        />
        <Input
          density="admin"
          label={kind === 'percent' ? 'Basis points (1000 = 10%)' : 'Amount off'}
          prefix={kind === 'percent' ? undefined : '₹'}
          inputMode={kind === 'percent' ? 'numeric' : 'decimal'}
          value={value}
          onChange={setValue}
          error={amountError}
        />
        <Input
          density="admin"
          label="Cap per booking"
          prefix="₹"
          inputMode="decimal"
          value={cap}
          onChange={setCap}
          helper={kind === 'percent' ? 'Required for a percentage' : 'Optional'}
        />
        <Input
          density="admin"
          label="Budget"
          prefix="₹"
          inputMode="decimal"
          value={budget}
          onChange={setBudget}
        />
        <Select
          density="admin"
          label="Who"
          options={['Anyone', 'First booking only']}
          value={firstOnly}
          onChange={setFirstOnly}
        />
        <Input
          density="admin"
          label="Uses per devotee"
          inputMode="numeric"
          value={perUser}
          onChange={setPerUser}
        />
      </div>
      <OfferScopeFields cityIds={cityIds} pujaIds={pujaIds} onCities={setCityIds} onPujas={setPujaIds} />
      {create.isError && (
        <ErrorState
          inline
          title="The server refused this campaign"
          cause={errorMessage(create.error)}
        />
      )}
      {needsSecond && (
        /* Revealed by the refusal rather than shown always: most campaigns never need a second
           pair of eyes, and a field that is usually irrelevant teaches operators to ignore it. */
        <Input
          density="admin"
          label="Second approver (admin id)"
          inputMode="numeric"
          value={seconder}
          onChange={setSeconder}
          helper="A different growth or superadmin — the server refuses your own id"
        />
      )}
      <ScopeAction
        scope={scope}
        need="growth"
        size="sm"
        disabled={!ready}
        onClick={() =>
          create.mutate(
            {
              slug: slug.trim(),
              name: name.trim(),
              title: title.trim(),
              terms: termLines(terms),
              offer_type: 'coupon',
              discount_kind: kind,
              discount_value: Number(amount),
              max_discount_paise: capPaise,
              budget_paise: budgetPaise,
              /* Sent explicitly rather than left to the server's defaults: who may claim a
                 discount and how often is the campaign, not a detail. */
              first_booking_only: firstOnly === 'First booking only',
              per_user_limit: Number(perUser) || 1,
              min_order_paise: 0,
              city_ids: cityIds,
              puja_ids: pujaIds,
              starts_at: new Date().toISOString(),
              ...(seconder === '' ? {} : { second_approver_admin_id: Number(seconder) }),
            },
            { onSuccess: onCreated },
          )
        }
      >
        Create campaign
      </ScopeAction>
    </Section>
  );
}

/**
 * The state changes an operator makes, keyed by where the campaign is now (`POST …/status`).
 *
 * The server accepts any settable status from any other, so the console is what keeps the
 * lifecycle honest: a draft goes live, a live campaign pauses, a paused one resumes, and any of
 * them can end. `ended` and `exhausted` offer nothing — a finished deal is replaced, not revived.
 */
export interface StatusMove {
  to: 'active' | 'paused' | 'ended';
  button: string;
  title: string;
  phrase: string;
  confirmLabel: string;
  tone: 'warn' | 'error';
  consequences: string[];
  done: string;
}

const GO_LIVE: StatusMove = {
  to: 'active',
  button: 'Go live',
  title: 'Put this offer live?',
  phrase: 'LIVE',
  confirmLabel: 'Go live',
  tone: 'warn',
  consequences: [
    'Devotees in its scope see the discount from their next search, and its codes start working.',
    'The budget starts burning; it stops itself when the budget or a limit runs out.',
  ],
  done: 'Offer is live',
};
const PAUSE: StatusMove = {
  to: 'paused',
  button: 'Pause offer',
  title: 'Pause this offer?',
  phrase: 'PAUSE',
  confirmLabel: 'Pause',
  tone: 'warn',
  consequences: [
    'Devotees stop seeing the discount immediately; nothing already booked changes.',
    'Held redemptions keep their budget until those bookings confirm or die.',
    'Resuming is a second act with its own reason — pausing is not a draft.',
  ],
  done: 'Offer paused',
};
const RESUME: StatusMove = {
  ...GO_LIVE,
  button: 'Resume offer',
  title: 'Resume this offer?',
  phrase: 'RESUME',
  confirmLabel: 'Resume',
  consequences: [
    'Devotees in its scope see the discount again from their next search.',
    'It picks up the budget where it left off — held and spent amounts carry over.',
  ],
  done: 'Offer resumed',
};
const END: StatusMove = {
  to: 'ended',
  button: 'End offer',
  title: 'End this offer for good?',
  phrase: 'END',
  confirmLabel: 'End offer',
  tone: 'error',
  consequences: [
    'Devotees stop seeing the discount and its codes stop working; nothing already booked changes.',
    'An ended campaign is not resumed. For the same deal again, create a new offer.',
  ],
  done: 'Offer ended',
};

export const STATUS_MOVES: Record<string, StatusMove[]> = {
  draft: [GO_LIVE, END],
  active: [PAUSE, END],
  paused: [RESUME, END],
};

/** The detail pane. Spend and codes are separate reads, so each says its own absence honestly. */
function OfferDetail({
  offer,
  onClose,
  onMove,
}: {
  offer: OfferRow;
  onClose: () => void;
  onMove: (move: StatusMove) => void;
}) {
  const { scope } = useConsole();
  const spend = useOfferSpend(offer.id);
  const [couponPage, setCouponPage] = useState(1);
  const coupons = useOfferCoupons(offer.id, couponPage);
  const [editing, setEditing] = useState(false);

  const scopeOptions = useOfferScopeOptions();
  const scopeRow = [
    scopeSummary(offer.city_ids, scopeOptions.data?.cities, 'every launched city'),
    scopeSummary(offer.puja_ids, scopeOptions.data?.pujas, 'every puja'),
  ].join(' · ');

  return (
    <DetailPage
      title={offer.name}
      code={offer.slug}
      status={<StatusChip tone={STATUS_TONE[offer.status] ?? 'neutral'} label={offer.status} />}
      onClose={onClose}
      actions={
        <>
          <ScopeAction
            scope={scope}
            need="growth"
            size="sm"
            variant="ghost"
            disabled={!spend.data}
            onClick={() => setEditing(true)}
          >
            Edit
          </ScopeAction>
          {(STATUS_MOVES[offer.status] ?? []).map((m) => (
            <ScopeAction
              key={m.to}
              scope={scope}
              need="growth"
              size="sm"
              variant={m.to === 'ended' ? 'ghost' : undefined}
              onClick={() => onMove(m)}
            >
              {m.button}
            </ScopeAction>
          ))}
        </>
      }
    >
      {editing && spend.data && (
        <OfferEditor
          key={JSON.stringify(offer)}
          offer={offer}
          redeemed={spend.data.redemption_count > 0}
          onDone={() => setEditing(false)}
        />
      )}
      <KV
        rows={[
          ['Takes off', describeDiscount(offer)],
          [
            'Cap per booking',
            offer.max_discount_paise === null ? (
              'uncapped'
            ) : (
              <Money key="c" paise={offer.max_discount_paise} />
            ),
          ],
          ['Minimum order', <Money key="m" paise={offer.min_order_paise} />],
          [
            'Budget',
            offer.budget_paise === null ? 'uncapped' : <Money key="b" paise={offer.budget_paise} />,
          ],
          [
            'Per devotee',
            `${offer.per_user_limit}${offer.first_booking_only ? ' · first booking only' : ''}`,
          ],
          [
            'Total uses',
            offer.total_redemption_limit === null
              ? 'unlimited'
              : String(offer.total_redemption_limit),
          ],
          ['Scope', scopeRow],
        ]}
      />

      <Section
        title="Spend"
        note="Held is committed to bookings that may still die; spent has gone"
      >
        {spend.isPending && <LoadingState rows={2} message="Reading spend…" />}
        {spend.isError && (
          <ErrorState inline title="Spend is unavailable" cause="The report did not load." />
        )}
        {spend.data && (
          <KV
            rows={[
              ['Held', <Money key="h" paise={spend.data.held_paise} />],
              ['Spent', <Money key="s" paise={spend.data.spent_paise} />],
              ['Redemptions', String(spend.data.redemption_count)],
              ...(spend.data.budget_paise
                ? [
                    [
                      'Burn',
                      <BurnBar
                        key="burn"
                        ratio={burnRatio(
                          spend.data.held_paise + spend.data.spent_paise,
                          spend.data.budget_paise,
                        )}
                      />,
                    ] as [string, React.ReactNode],
                  ]
                : []),
            ]}
          />
        )}
      </Section>

      <OfferRedemptions offerId={offer.id} />

      <MintCode offerId={offer.id} takesCodes={offer.offer_type !== 'auto_apply'} />

      <Section title="Codes" pad={false}>
        {coupons.isPending && <LoadingState rows={2} message="Reading codes…" />}
        {coupons.isError && (
          <ErrorState inline title="Codes are unavailable" cause="The list did not load." />
        )}
        {coupons.data && coupons.data.items.length === 0 && (
          <EmptyState
            inline
            icon="percent"
            title="No codes"
            body={
              offer.offer_type === 'auto_apply'
                ? 'This offer applies without a code.'
                : 'Nothing has been minted under this campaign yet.'
            }
          />
        )}
        {coupons.data && coupons.data.items.length > 0 && (
          <DataTable
            density="compact"
            columns={[
              { key: 'code', label: 'Code', mono: true },
              {
                key: 'per_user',
                label: 'Per devotee',
                align: 'right',
                mono: true,
              },
              { key: 'total', label: 'Total', align: 'right', mono: true },
              { key: 'state', label: 'State' },
            ]}
            rows={coupons.data.items.map((c) => ({
              id: c.id,
              code: c.code,
              per_user: c.per_user_max === null ? 'inherits' : String(c.per_user_max),
              total: c.max_redemptions === null ? 'inherits' : String(c.max_redemptions),
              state: (
                <StatusChip
                  tone={c.is_active ? 'success' : 'neutral'}
                  label={c.is_active ? 'active' : 'off'}
                />
              ),
            }))}
          />
        )}
        {coupons.data && (
          <Pager
            page={couponPage}
            pageSize={coupons.data.page_size}
            total={coupons.data.total}
            onPage={setCouponPage}
          />
        )}
      </Section>

      <div style={{ display: 'grid', gap: 6 }}>
        <Kicker>Public copy — linted</Kicker>
        <div
          style={{
            border: '1px solid var(--c-border)',
            background: 'var(--c-surface2)',
            borderRadius: 'var(--r-tile)',
            padding: 'var(--sp-md)',
            fontSize: 'var(--fs-admin-ui)',
            color: 'var(--c-ink)',
          }}
        >
          {offer.title}
        </div>
        <span
          style={{
            fontSize: 'var(--fs-admin-colhead)',
            color: 'var(--c-ink3)',
          }}
        >
          The lint blocks outcome claims and false urgency — no countdowns, no &ldquo;prices
          rising&rdquo;.
        </span>
      </div>

      <AuditTrailPanel entityType="offer" entityId={offer.id} />
    </DetailPage>
  );
}

/** The closed vocabulary a ruling is recorded in — free text cannot answer "why did we reject
    forty referrals in Patna last week", and that is the question somebody asks a month later. */
const REJECT_REASONS = [
  'self_referral',
  'device_match',
  'payment_instrument_match',
  'velocity_abuse',
  'booking_refunded',
  'ops_decision',
];

const REFERRAL_TONE: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
  signed_up: 'neutral',
  first_booking_confirmed: 'warn',
  qualified: 'success',
  rewarded: 'success',
  rejected: 'error',
};

/**
 * The programme dashboard and the fraud queue.
 *
 * **The ruling reason is a Select over the server's enum, not the confirm dialog's free text.**
 * The endpoint takes one of six values; a typed sentence would be refused, and a dialog that
 * collected one would be asking for something it could not use.
 *
 * **Requalify appears only on a rejected referral**, because that is the only status the server
 * accepts it for — an always-visible button would be a door the API refuses to open.
 */
export function ReferralsTab() {
  const { scope, toast } = useConsole();
  const [statusFilter, setStatusFilter] = useState('');
  const [ruling, setRuling] = useState<number | null>(null);
  const [reason, setReason] = useState('self_referral');
  const [openId, setOpenId] = useState<number | null>(null);

  const referrals = useReferrals({
    page: 1,
    page_size: PAGE_SIZE,
    status: statusFilter || undefined,
  });
  const detail = useReferral(openId);
  const reject = useRejectReferral();
  const requalify = useRequalifyReferral();

  const items = referrals.data?.items ?? [];

  return (
    <>
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
        {['', 'signed_up', 'first_booking_confirmed', 'rewarded', 'rejected'].map((value) => (
          <Chip
            key={value || 'all'}
            variant="filter"
            label={value === '' ? 'All' : value.replace(/_/g, ' ')}
            selected={statusFilter === value}
            onClick={() => setStatusFilter(value)}
          />
        ))}
      </div>

      {referrals.isPending && <LoadingState rows={4} message="Reading introductions…" />}
      {referrals.isError && (
        <ErrorState title="Referrals are unavailable" cause="The list did not load." />
      )}
      {referrals.data && items.length === 0 && (
        <EmptyState icon="users" title="No introductions yet" body="Nobody has used a code." />
      )}
      {referrals.data && items.length > 0 && (
        <Section
          title="Referral programme"
          note="Rulings are reasoned and audited · a hold is not an accusation"
          pad={false}
        >
          <DataTable
            density="comfortable"
            onRowClick={(row) => setOpenId(Number(row.id))}
            footer={
              <span>
                {referrals.data.total} introductions · rewards vest after the issue window
              </span>
            }
            columns={[
              { key: 'code', label: 'Code used', mono: true },
              { key: 'referrer', label: 'Referrer', mono: true },
              { key: 'referee', label: 'Referee', mono: true },
              { key: 'status', label: 'State' },
              { key: 'act', label: '' },
            ]}
            rows={items.map((r) => ({
              id: r.id,
              code: r.referral_code_used,
              referrer: `#${r.referrer_user_id}`,
              referee: `#${r.referee_user_id}`,
              status: (
                <StatusChip
                  tone={REFERRAL_TONE[r.status] ?? 'neutral'}
                  label={r.status.replace(/_/g, ' ')}
                />
              ),
              act:
                r.status === 'rejected' ? (
                  <ScopeAction
                    scope={scope}
                    need="growth"
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      requalify.mutate(r.id, {
                        onSuccess: () =>
                          toast({
                            tone: 'success',
                            message: 'Rejection overturned',
                          }),
                        onError: (error) =>
                          toast({
                            tone: 'error',
                            message: errorMessage(error as ApiError),
                          }),
                      })
                    }
                  >
                    Requalify
                  </ScopeAction>
                ) : (
                  <ScopeAction
                    scope={scope}
                    need="growth"
                    size="sm"
                    variant="ghost"
                    onClick={() => setRuling(r.id)}
                  >
                    Rule
                  </ScopeAction>
                ),
            }))}
          />
        </Section>
      )}

      {openId !== null && (
        <Section title={`Introduction #${openId}`} note="What it paid, on each side">
          {detail.isPending && <LoadingState rows={2} message="Reading the rewards…" />}
          {detail.isError && (
            <ErrorState title="Rewards are unavailable" cause="The detail did not load." />
          )}
          {detail.data && detail.data.rewards.length === 0 && (
            <EmptyState
              icon="clock"
              title="Nothing issued yet"
              body="The reward vests once the issue window closes with no refund."
            />
          )}
          {detail.data && detail.data.rewards.length > 0 && (
            <KV
              rows={detail.data.rewards.map((reward) => [
                reward.beneficiary,
                `${reward.reward_kind} · ${reward.status}${
                  reward.punya_points === null ? '' : ` · ${reward.punya_points} Punya`
                }`,
              ])}
            />
          )}
          <ScopeAction
            scope={scope}
            need="growth"
            size="sm"
            variant="ghost"
            onClick={() => setOpenId(null)}
          >
            Close
          </ScopeAction>
        </Section>
      )}

      {ruling !== null && (
        <Section
          title="Record a ruling"
          note="One of six reasons — the queue has to stay countable"
        >
          <Select
            density="admin"
            label="Reason"
            options={REJECT_REASONS}
            value={reason}
            onChange={setReason}
          />
        </Section>
      )}

      {/* Rendered only when there IS a referral to rule on, rather than mounted always with an
          `open` flag. Hoisted, it needed a `ruling === null` guard inside the handler that the
          UI could never make true — a branch existing only to reassure the compiler. */}
      {ruling !== null && (
        <ConfirmAction
          open
          title="Reject this referral?"
          phrase="REJECT"
          tone="warn"
          consequences={[
            'Anything still pending is cancelled; anything already issued stays issued.',
            'Taking back a reward that was paid is a reversal with its own row, not this.',
            'Rulings are audited and sampled — a wrong hold is as costly as a wrong payout.',
          ]}
          onCancel={() => setRuling(null)}
          onConfirm={() => {
            setRuling(null);
            reject.mutate(
              { referralId: ruling, reason },
              {
                onSuccess: () => toast({ tone: 'info', message: 'Ruling recorded' }),
                onError: (error) =>
                  toast({
                    tone: 'error',
                    message: errorMessage(error as ApiError),
                  }),
              },
            );
          }}
          confirmLabel="Record ruling"
        />
      )}
    </>
  );
}

/**
 * The Punya liability and the goodwill instrument.
 *
 * **The rate comes from the report, never from a constant here.** `punya.redeem_paise_per_point`
 * is a growth-owned setting that moves; a console multiplying points by its own copy would print
 * a liability that disagreed with finance's the first time somebody changed the dial.
 *
 * **The monthly issued/redeemed/expired ledger this tab used to show is GONE, not ported.** No
 * endpoint yields it, and a screen with no endpoint ships honestly absent rather than as a
 * plausible table nobody can trace to a row. What the API does answer — the outstanding balance,
 * in both units — is here instead.
 *
 * **A statement is looked up by user id because that is the identifier the API takes.** A phone
 * box that could not resolve to one would be a search that never searched.
 */
export function PunyaTab() {
  const { scope, toast } = useConsole();
  const [lookup, setLookup] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [points, setPoints] = useState('');
  const [direction, setDirection] = useState('Credit');
  const [memo, setMemo] = useState('');
  const [confirming, setConfirming] = useState(false);

  const liability = usePunyaLiability();
  const statement = usePunyaStatement(userId);
  const adjust = useAdjustPunya();

  const parsedPoints = digits(points);
  /* Read from the BOX, not from `userId`. `userId` is set by the Look-up button, and gating the
     submit on it made a loop: the button that would have set it was disabled until it was set. */
  const parsedUser = digits(lookup);
  /* No seconder box. Submitting parks the request; a different finance admin countersigns it
     from their own session on the Approvals screen, so there is nobody to name here. */
  const ready =
    parsedUser !== null && parsedPoints !== null && parsedPoints > 0 && memo.trim().length >= 3;
  /* What the dialog is about to submit, or null. Carrying the two numbers rather than a flag is
     what lets the handler use them without a null guard the disabled button can never make true. */
  const submitting =
    confirming && parsedUser !== null && parsedPoints !== null
      ? { user: parsedUser, points: parsedPoints }
      : null;

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile
          label="Outstanding Punya"
          value={liability.data ? String(liability.data.outstanding_points) : '—'}
          note="points devotees hold"
        />
        <StatTile
          label="Carried liability"
          value={
            liability.data ? (
              <Money paise={liability.data.outstanding_paise} style={{ textAlign: 'left' }} />
            ) : (
              '—'
            )
          }
          note="what finance carries it at"
        />
        <StatTile
          label="Redemption rate"
          value={
            liability.data ? (
              <Money paise={liability.data.redeem_paise_per_point} style={{ textAlign: 'left' }} />
            ) : (
              '—'
            )
          }
          note="per point — a server dial, not a constant"
        />
        <StatTile
          label="Accounts"
          value={liability.data ? String(liability.data.accounts) : '—'}
          note="created on a devotee's first movement"
        />
      </div>
      {liability.isPending && <LoadingState rows={1} message="Reading the liability…" />}
      {liability.isError && (
        <ErrorState title="The liability report is unavailable" cause="It did not load." />
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: 'var(--sp-lg)',
          alignItems: 'start',
        }}
      >
        <Section
          title="Statement"
          note="The history is the truth; the balance is a cache of it"
          pad={false}
        >
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-md)',
              padding: 'var(--sp-md)',
              alignItems: 'end',
            }}
          >
            <Input
              density="admin"
              label="Devotee (user id)"
              value={lookup}
              onChange={setLookup}
              placeholder="1042"
              style={{ flex: 1 }}
            />
            <ScopeAction
              scope={scope}
              need="growth"
              size="sm"
              variant="secondary"
              onClick={() => setUserId(digits(lookup))}
            >
              Look up
            </ScopeAction>
          </div>
          {statement.isPending && userId !== null && (
            <LoadingState rows={3} message="Reading the statement…" />
          )}
          {statement.isError && (
            <ErrorState title="The statement is unavailable" cause="It did not load." />
          )}
          {statement.data && statement.data.account === null && (
            <EmptyState
              icon="clock"
              title="No Punya account"
              body="This devotee has never earned or spent a point, so no account exists yet."
            />
          )}
          {statement.data?.account && (
            <DataTable
              density="compact"
              footer={
                <span>
                  {statement.data.total} movements · balance {statement.data.account.balance_points}{' '}
                  · earned {statement.data.account.lifetime_earned} · redeemed{' '}
                  {statement.data.account.lifetime_redeemed}
                </span>
              }
              columns={[
                { key: 'type', label: 'Entry' },
                { key: 'points', label: 'Points', align: 'right', mono: true },
                {
                  key: 'after',
                  label: 'Balance after',
                  align: 'right',
                  mono: true,
                },
                { key: 'source', label: 'Source' },
                { key: 'memo', label: 'Memo' },
              ]}
              rows={statement.data.items.map((row) => ({
                id: row.id,
                type: row.entry_type,
                /* SIGNED, straight from the row. A reversal credits or debits depending on what
                   it compensates, so the direction can never be re-derived from `entry_type`. */
                points: row.points > 0 ? `+${row.points}` : String(row.points),
                after: String(row.balance_after),
                source: row.source,
                memo: row.memo ?? '—',
              }))}
            />
          )}
        </Section>

        <Section
          title="Manual adjustment"
          note="Four eyes at any size — there is no amount one person may move alone"
        >
          <Input
            density="admin"
            label="Devotee (user id)"
            value={lookup}
            onChange={setLookup}
            placeholder="1042"
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 'var(--sp-md)',
            }}
          >
            <Select
              density="admin"
              label="Direction"
              options={['Credit', 'Debit']}
              value={direction}
              onChange={setDirection}
            />
            <Input
              density="admin"
              label="Points"
              value={points}
              onChange={setPoints}
              inputMode="numeric"
              placeholder="500"
            />
          </div>
          <Input
            density="admin"
            multiline
            label="Memo (mandatory)"
            value={memo}
            onChange={setMemo}
            placeholder="Goodwill after a delayed ceremony — TKT-88209"
          />
          <ScopeAction
            scope={scope}
            need="finance"
            size="sm"
            disabled={!ready}
            onClick={() => {
              setUserId(parsedUser);
              setConfirming(true);
            }}
          >
            Submit adjustment
          </ScopeAction>
        </Section>
      </div>

      {submitting !== null && (
        <ConfirmAction
          open
          title="Adjust a Punya balance?"
          phrase="ADJUST"
          tone="error"
          consequences={[
            'Moves loyalty credit on a devotee account and changes the platform liability.',
            'Nothing moves yet: a second finance admin, not you, countersigns it on the Approvals screen.',
            'The memo is stored with the entry and shown in the monthly liability review.',
          ]}
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false);
            adjust.mutate(
              {
                user_id: submitting.user,
                points: submitting.points,
                credit: direction === 'Credit',
                memo: memo.trim(),
              },
              {
                onSuccess: () => {
                  setPoints('');
                  setMemo('');
                  toast({ tone: 'info', message: 'Adjustment parked for countersign' });
                },
                onError: (error) =>
                  toast({
                    tone: 'error',
                    message: errorMessage(error as ApiError),
                  }),
              },
            );
          }}
          confirmLabel="Submit adjustment"
        />
      )}
    </>
  );
}

export function Offers() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('offers', TAB_KEYS, 'offers');
  const [move, setMove] = useState<StatusMove | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  /* Filtered on the SERVER — a client-side filter would search the page in hand and confidently
     report no match on the campaigns it had not fetched. */
  const [statusFilter, setStatusFilter] = useState('');

  const offers = useOffers({
    page: 1,
    page_size: PAGE_SIZE,
    status: statusFilter || undefined,
  });
  const setStatus = useSetOfferStatus();
  /* The four-eyes ceiling is a SERVER dial. Read it, never restate it — a hard-coded copy is
     right until the day somebody moves it, and then it is wrong next to a button. */
  const cap = useSetting('offers.four_eyes_budget_paise');
  const capValue = cap.data?.setting?.value;
  const capPaise = typeof capValue === 'number' ? capValue : null;

  const rows = offers.data?.items ?? [];
  const open = openId === null ? null : (rows.find((o) => o.id === openId) ?? null);
  const liveCount = rows.filter((o) => o.status === 'active').length;

  if (open) {
    /* The pause dialog lives HERE rather than beside the others, so the offer it acts on is in
       scope by construction. Hoisted, it needed an `if (open)` guard that the UI can never make
       false — a branch that exists only to reassure the compiler. */
    return (
      <>
        <OfferDetail offer={open} onClose={() => setOpenId(null)} onMove={setMove} />
        <ConfirmAction
          open={move !== null}
          title={move?.title ?? ''}
          phrase={move?.phrase ?? ''}
          tone={move?.tone}
          reason
          consequences={move?.consequences ?? []}
          onCancel={() => setMove(null)}
          onConfirm={(reason) => {
            const m = move!;
            setMove(null);
            setStatus.mutate(
              { offerId: open.id, status: m.to, reason },
              {
                onSuccess: () => toast({ tone: 'success', message: m.done }),
                onError: (e) => toast({ tone: 'error', message: `The server refused the change: ${errorMessage(e)}` }),
              },
            );
          }}
          confirmLabel={move?.confirmLabel ?? ''}
        />
      </>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: 'var(--sp-md)',
        }}
      >
        <StatTile
          label="Live offers"
          value={String(liveCount)}
          note={`${count(offers.data?.total ?? 0, 'campaign')} in all`}
        />
        <StatTile
          label="Second approval above"
          value={capPaise === null ? '—' : <Money paise={capPaise} style={{ textAlign: 'left' }} />}
          note={
            capPaise === null ? 'reading the settings row' : 'a budget over this needs two people'
          }
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-sm)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {TABS.map((t) => (
          <Chip
            key={t.key}
            variant="filter"
            label={t.label}
            selected={tab === t.key}
            onClick={() => setTab(t.key)}
          />
        ))}
        <span style={{ flex: 1 }} />
        {tab === 'offers' && (
          <ScopeAction
            scope={scope}
            need="growth"
            size="sm"
            icon="plus"
            onClick={() => setCreating((v) => !v)}
          >
            New offer
          </ScopeAction>
        )}
      </div>

      {tab === 'offers' && creating && <NewOffer onCreated={() => setCreating(false)} />}

      {tab === 'offers' && (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
          {['', 'active', 'paused', 'exhausted', 'draft'].map((value) => (
            <Chip
              key={value || 'all'}
              variant="filter"
              label={value === '' ? 'All' : value}
              selected={statusFilter === value}
              onClick={() => setStatusFilter(value)}
            />
          ))}
        </div>
      )}

      {tab === 'offers' && offers.isPending && (
        <LoadingState rows={4} message="Reading campaigns…" />
      )}
      {tab === 'offers' && offers.isError && (
        <ErrorState title="Campaigns are unavailable" cause="The list did not load." />
      )}
      {tab === 'offers' && offers.data && offers.data.items.length === 0 && (
        <EmptyState icon="percent" title="No campaigns yet" body="Nothing has been created." />
      )}
      {tab === 'offers' && offers.data && offers.data.items.length > 0 && (
        <DataTable
          density="comfortable"
          onRowClick={(row) => setOpenId(Number(row.id))}
          footer={
            <span>{count(offers.data.total, 'campaign')} · budget caps are hard stops, not warnings</span>
          }
          columns={[
            { key: 'slug', label: 'Offer', mono: true },
            { key: 'name', label: 'Name' },
            { key: 'takes', label: 'Takes off', align: 'right', mono: true },
            { key: 'budget', label: 'Budget', align: 'right', mono: true },
            { key: 'status', label: 'State' },
          ]}
          rows={offers.data.items.map((o) => ({
            id: o.id,
            slug: o.slug,
            name: o.name,
            takes: describeDiscount(o),
            budget: o.budget_paise === null ? 'uncapped' : <Money paise={o.budget_paise} />,
            status: <StatusChip tone={STATUS_TONE[o.status] ?? 'neutral'} label={o.status} />,
          }))}
        />
      )}

      {tab === 'referrals' && <ReferralsTab />}

      {tab === 'punya' && <PunyaTab />}

      {tab === 'abuse' && <AbuseTab />}
    </>
  );
}
